// Privacy guards — the hard switches behind her senses.
//
// Every one of these is a gate, not a preference: when a guard is off the
// subsystem behind it refuses to start, so "microphone off" means no ear at
// all rather than a quiet one. Local, sticky across reloads, and readable at a
// glance from the Privacy & Permissions dashboard.

import { getOption, setOption, subscribeOptions } from "./persistOptions";
import { logAudit } from "./auditLog";

export type GuardId = "mic" | "camera" | "recording" | "rf" | "learning" | "notify";

export interface GuardDef {
  id: GuardId;
  label: string;
  detail: string;
  /** What breaks when this is off — shown plainly on the dashboard. */
  offMeans: string;
  defaultOn: boolean;
}

export const GUARDS: GuardDef[] = [
  { id: "mic", label: "Microphone", detail: "Her ear: wake word, dictation, hydrophone and the audio side of RF sweeps.", offMeans: "She cannot listen at all — no wake word, no voice notes, no audio learning.", defaultOn: true },
  { id: "camera", label: "Cameras & optics", detail: "Webcams, telescopes and microscopes for live watching.", offMeans: "Every live view stays dark, including security monitoring.", defaultOn: true },
  { id: "recording", label: "Clip recording", detail: "Writing motion and intruder clips to this device's storage.", offMeans: "She still watches and warns, but nothing is written to disk.", defaultOn: true },
  { id: "rf", label: "RF scanning", detail: "Passive spectrum sweeps that build the airwave map.", offMeans: "No sweeps, no frequency log, no new airwave nodes.", defaultOn: false },
  { id: "learning", label: "Local learning", detail: "Folding what she sees and hears into her own memory.", offMeans: "She answers from what she already holds and learns nothing new.", defaultOn: true },
  { id: "notify", label: "Alerts & notifications", detail: "Desktop and in-app alerts when something matters.", offMeans: "Events are still logged, but nothing interrupts you.", defaultOn: true },
];

const key = (id: GuardId) => `guard:${id}`;

export function guardAllowed(id: GuardId): boolean {
  const def = GUARDS.find((g) => g.id === id);
  return getOption<boolean>(key(id), def?.defaultOn ?? true) === true;
}

export function guards(): Record<GuardId, boolean> {
  const out = {} as Record<GuardId, boolean>;
  for (const g of GUARDS) out[g.id] = guardAllowed(g.id);
  return out;
}

export function setGuard(id: GuardId, on: boolean): void {
  setOption(key(id), on);
  const def = GUARDS.find((g) => g.id === id);
  void logAudit(on ? "consent" : "purge", `${def?.label ?? id} ${on ? "allowed" : "blocked"}`,
    on ? `${def?.label ?? id} switched on from Privacy & Permissions.` : def?.offMeans ?? "Switched off.");
}

export function subscribeGuards(fn: (g: Record<GuardId, boolean>) => void): () => void {
  fn(guards());
  return subscribeOptions(() => fn(guards()));
}

/** Everything off at once — her panic switch for privacy. */
export function blackout(): void {
  for (const g of GUARDS) setGuard(g.id, false);
}

// -------------------------------------------------------- browser permissions

export type PermState = "granted" | "denied" | "prompt" | "unknown";

export interface BrowserPerms {
  microphone: PermState;
  camera: PermState;
  geolocation: PermState;
  notifications: PermState;
}

async function query(name: string): Promise<PermState> {
  try {
    const p = navigator.permissions as unknown as { query: (d: { name: string }) => Promise<{ state: PermState }> } | undefined;
    if (!p?.query) return "unknown";
    return (await p.query({ name })).state ?? "unknown";
  } catch { return "unknown"; }
}

export async function browserPerms(): Promise<BrowserPerms> {
  if (typeof navigator === "undefined") {
    return { microphone: "unknown", camera: "unknown", geolocation: "unknown", notifications: "unknown" };
  }
  const notif = typeof Notification !== "undefined"
    ? (Notification.permission === "default" ? "prompt" : (Notification.permission as PermState))
    : "unknown";
  const [microphone, camera, geolocation] = await Promise.all([
    query("microphone"), query("camera"), query("geolocation"),
  ]);
  return { microphone, camera, geolocation, notifications: notif };
}

/** Ask the browser for desktop notifications — needs a user gesture. */
export async function askNotifications(): Promise<PermState> {
  try {
    if (typeof Notification === "undefined") return "unknown";
    const res = await Notification.requestPermission();
    return res === "default" ? "prompt" : (res as PermState);
  } catch { return "unknown"; }
}
